import { TableRow, TableCell } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { IntuiTableRowActions } from './IntuiTableRowActions'
import type { IntuiColumnDef, RowAction } from './types'

interface IntuiTableRowProps<T> {
  row: T
  rowIndex: number
  rowId: string
  columns: IntuiColumnDef<T>[]
  compact?: boolean
  striped?: boolean
  bordered?: boolean
  // Selection
  selectable?: boolean
  selectionMode?: 'single' | 'multi'
  isSelected: boolean
  isRowSelectable: boolean
  onSelectRow: (rowId: string) => void
  // Expand
  expandable?: boolean
  isExpanded: boolean
  onToggleExpand: (rowId: string) => void
  getExpandedContent?: (row: T) => React.ReactNode
  // Interaction
  onRowClick?: (row: T) => void
  onRowDoubleClick?: (row: T) => void
  onRowContextMenu?: (row: T, event: React.MouseEvent) => void
  isDisabled: boolean
  rowClassName?: string
  // Actions
  rowActions?: RowAction<T>[]
  // Layout
  totalColumnCount: number
}

export function IntuiTableRow<T>({
  row,
  rowIndex,
  rowId,
  columns,
  compact,
  striped,
  bordered,
  selectable,
  selectionMode,
  isSelected,
  isRowSelectable,
  onSelectRow,
  expandable,
  isExpanded,
  onToggleExpand,
  getExpandedContent,
  onRowClick,
  onRowDoubleClick,
  onRowContextMenu,
  isDisabled,
  rowClassName,
  rowActions,
  totalColumnCount,
}: IntuiTableRowProps<T>) {
  const isClickable = !!onRowClick && !isDisabled
  // Figma: cells use p-2 (8px) padding, min-w-[85px]
  const cellPadding = compact ? 'px-2 py-1' : 'p-2'

  const handleRowClick = (e: React.MouseEvent) => {
    if (isDisabled) return
    // Don't fire row click if the click was on an interactive element
    const target = e.target as HTMLElement
    if (target.closest('button, input, [role="checkbox"], [role="menuitem"]'))
      return
    onRowClick?.(row)
  }

  return (
    <>
      {/* Figma: border-b border-border on each row, hover:bg-muted/50 */}
      <TableRow
        data-state={isSelected ? 'selected' : undefined}
        className={cn(
          'border-b border-border transition-colors',
          isClickable && 'cursor-pointer',
          isDisabled && 'opacity-50 cursor-not-allowed',
          striped && rowIndex % 2 === 1 && 'bg-muted/30',
          bordered && '[&>td]:border-r [&>td:last-child]:border-r-0',
          rowClassName
        )}
        onClick={handleRowClick}
        onDoubleClick={
          onRowDoubleClick && !isDisabled
            ? () => onRowDoubleClick(row)
            : undefined
        }
        onContextMenu={
          onRowContextMenu && !isDisabled
            ? (e) => {
                e.preventDefault()
                onRowContextMenu(row, e)
              }
            : undefined
        }
      >
        {/* Selection cell */}
        {selectable && (
          <TableCell className={cn('w-10', cellPadding)}>
            {selectionMode === 'multi' ? (
              <Checkbox
                checked={isSelected}
                disabled={!isRowSelectable || isDisabled}
                onCheckedChange={() => onSelectRow(rowId)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select row ${rowId}`}
              />
            ) : (
              <input
                type="radio"
                checked={isSelected}
                disabled={!isRowSelectable || isDisabled}
                onChange={() => onSelectRow(rowId)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 accent-primary"
                aria-label={`Select row ${rowId}`}
              />
            )}
          </TableCell>
        )}

        {/* Expand cell */}
        {expandable && (
          <TableCell className={cn('w-10', cellPadding)}>
            {getExpandedContent ? (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleExpand(rowId)
                }}
                aria-expanded={isExpanded}
                aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            ) : null}
          </TableCell>
        )}

        {/* Data cells — Figma: text-sm, font-normal, text-foreground, overflow ellipsis, border-b */}
        {columns.map((column) => (
          <TableCell
            key={column.key}
            className={cn(
              cellPadding,
              'text-sm font-normal text-foreground min-w-[85px]',
              column.align === 'center' && 'text-center',
              column.align === 'right' && 'text-right',
              column.className
            )}
            style={{
              width: column.width,
              minWidth: column.minWidth ?? '85px',
            }}
          >
            <div className={cn(
              'flex flex-col justify-center min-h-[20px]',
              column.align === 'right' && 'items-end',
              column.align === 'center' && 'items-center',
            )}>
              {column.cell(row, rowIndex)}
            </div>
          </TableCell>
        ))}

        {/* Actions cell */}
        {rowActions && rowActions.length > 0 && (
          <TableCell className={cn('w-12', cellPadding)}>
            <IntuiTableRowActions row={row} actions={rowActions} />
          </TableCell>
        )}
      </TableRow>

      {/* Expanded content row */}
      {expandable && isExpanded && getExpandedContent && (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={totalColumnCount} className="p-0">
            <div className="px-4 py-3 bg-muted/20">
              {getExpandedContent(row)}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
